import React from 'react';
import { LuCalculator } from "react-icons/lu";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import "./EmiCalculator.css";

ChartJS.register(ArcElement, Tooltip, Legend);

const EmiCalculator = ({ calcData, setCalcData, emiResults }) => {
  const {
    loanAmount = 0,
    emi = 0,
    totalInterest = 0,
    totalAmount = 0,
    processingFeeAmount = 0
  } = emiResults || {};

  const handleSliderChange = (e) => {
    const { name, value } = e.target;
    setCalcData(prev => ({ ...prev, [name]: Number(value) }));
  };

  const pieData = {
    labels: ["Principal Loan Amount", "Interest"],
    datasets: [{
      data: [loanAmount, totalInterest],
      backgroundColor: ["#F59E0B", "#EA580C"],
      hoverBackgroundColor: ["#FBBF24", "#D97706"],
      borderWidth: 0,
    }],
  };

  const pieOptions = {
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: { size: 12, family: "'Inter', sans-serif" },
          color: '#64748B',
          boxWidth: 12,
        },
      },
      tooltip: {
        backgroundColor: '#FFFFFF',
        titleColor: '#1E293B',
        bodyColor: '#475569',
        borderColor: '#E5E7EB',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 10,
        callbacks: {
          label: (context) => `₹${context.raw.toLocaleString()}`,
        },
      },
    },
    cutout: '65%',
    maintainAspectRatio: false,
  };

  return (
    <div className="emi-calc-wrapper">
      <div className="calculator-section">
        <div className="calc-header">
          <div className="icon-container">
            <LuCalculator className="calculator-icon" />
          </div>
          <div className="header-text">
            <h2 className="title">EMI Calculator</h2>
            <p className="description">Plan your loan payments with ease</p>
          </div>
        </div>

        <div className="inputs">
          <div className="input-group">
            <div className="label-row">
              <label className="label">Product Price</label>
              <span className="value-display">₹{calcData.productPrice.toLocaleString()}</span>
            </div>
            <input
              type="range"
              min={10000}
              max={200000}
              step={1000}
              name="productPrice"
              value={calcData.productPrice}
              onChange={handleSliderChange}
              className="slider"
              aria-label="Product Price Slider"
            />
          </div>

          <div className="input-group">
            <div className="label-row">
              <label className="label">Down Payment</label>
              <span className="value-display">{calcData.downPayment}%</span>
            </div>
            <input
              type="range"
              min={20}
              max={30}
              step={1}
              name="downPayment"
              value={calcData.downPayment}
              onChange={handleSliderChange}
              className="slider"
              aria-label="Down Payment Slider"
            />
          </div>

          <div className="input-group">
            <div className="label-row">
              <label className="label">Tenure</label>
              <span className="value-display">{calcData.tenure} months</span>
            </div>
            <input
              type="range"
              min={3}
              max={6}
              step={1}
              name="tenure"
              value={calcData.tenure}
              onChange={handleSliderChange}
              className="slider"
              aria-label="Tenure Slider"
            />
          </div>

          <div className="input-group">
            <div className="label-row">
              <label className="label">Interest Rate</label>
              <span className="value-display">{calcData.interestRate}%</span>
            </div>
            <input
              type="range"
              min={18}
              max={36}
              step={1}
              name="interestRate"
              value={calcData.interestRate}
              onChange={handleSliderChange}
              className="slider"
              aria-label="Interest Rate Slider"
            />
          </div>

          {/* Processing Fee Slider COMPLETELY REMOVED from inputs */}
        </div>

        <div className="summary">
          <div className="summary-header">Loan Summary</div>
          <div className="summary-item">
            <span className="summary-label">Loan Amount:</span>
            <span className="summary-value">₹{loanAmount.toLocaleString()}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Processing Fee:</span>
            <span className="summary-value">₹{processingFeeAmount.toLocaleString()}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Interest Rate:</span>
            <span className="summary-value">{calcData.interestRate}%</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Tenure:</span>
            <span className="summary-value">{calcData.tenure} months</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Monthly EMI:</span>
            <span className="summary-value positive">₹{emi.toLocaleString()}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Total Interest:</span>
            <span className="summary-value">₹{totalInterest.toLocaleString()}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Total Payment:</span>
            <span className="summary-value">₹{totalAmount.toLocaleString()}</span>
          </div>
        </div>

        <div className="chart-section">
          <h3 className="chart-title">Payment Breakdown</h3>
          <div className="pie-wrapper">
            <Pie data={pieData} options={pieOptions} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmiCalculator;